import { Check, Download, Moon, Plus, Sun, Upload } from "lucide-react";
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { ThemeEditor } from "@/components/theme/ThemeEditor";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { usePreferences } from "@/hooks/usePreferences";
import { cn } from "@/lib/utils";
import {
  allThemes,
  BUILT_IN_THEMES,
  cloneAppearance,
  createSharedThemeFile,
  createThemeId,
  DEFAULT_APPEARANCE,
  isBuiltInTheme,
  MAX_SAVED_THEMES,
  normalizeAppearance,
  parseSharedThemeFile,
  selectTheme,
  type AppearancePreferences,
  type SavedTheme,
  type ThemeMode,
} from "@/lib/theme";

const MAX_THEME_FILE_SIZE = 64 * 1024;

function ThemeSwatches({ theme }: { theme: SavedTheme }) {
  return (
    <span className="flex items-center -space-x-1" aria-hidden="true">
      {[theme.colors.background, theme.colors.surface, theme.colors.accent].map((color, index) => (
        <span key={`${color}-${index}`} className="h-5 w-5 rounded-full border-2 border-studio-panel" style={{ backgroundColor: color }} />
      ))}
    </span>
  );
}

function ThemeCard({ theme, selected, onClick }: { theme: SavedTheme; selected: boolean; onClick: () => void }) {
  const ModeIcon = theme.mode === "light" ? Sun : Moon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group flex min-h-24 flex-col justify-between rounded-xl border p-3 text-left transition-all focus-visible:outline-none focus-visible:shadow-glow-sm",
        selected
          ? "border-studio-accent bg-studio-accent-soft shadow-glow-sm"
          : "border-studio-border bg-studio-panel hover:-translate-y-0.5 hover:border-studio-border-light hover:shadow-panel"
      )}
    >
      <div className="flex w-full items-start justify-between gap-2">
        <ThemeSwatches theme={theme} />
        {selected ? <Check className="h-4 w-4 text-studio-accent" /> : <ModeIcon className="h-3.5 w-3.5 text-studio-muted" />}
      </div>
      <div className="mt-3 min-w-0">
        <p className="truncate text-xs font-semibold text-studio-ink">{theme.name}</p>
        <p className="mt-0.5 font-mono text-[9px] uppercase tracking-wide text-studio-muted">
          {isBuiltInTheme(theme.id) ? "Built in" : "Personal"} · {theme.mode}
        </p>
      </div>
    </button>
  );
}

export function AppearanceSettingsPage() {
  const { preferences, isLoading, update } = usePreferences();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageIsError, setMessageIsError] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newThemeName, setNewThemeName] = useState("My Theme");
  const [newThemeBase, setNewThemeBase] = useState<ThemeMode>("light");
  const [appearanceDraft, setAppearanceDraft] = useState<AppearancePreferences>(() => cloneAppearance(DEFAULT_APPEARANCE));
  const [appearanceDirty, setAppearanceDirty] = useState(false);
  const themeFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!preferences || appearanceDirty) return;
    setAppearanceDraft(cloneAppearance(normalizeAppearance(preferences.appearance)));
  }, [appearanceDirty, preferences]);

  const themes = useMemo(() => allThemes(appearanceDraft), [appearanceDraft]);
  const selectedTheme = themes.find((theme) => theme.id === appearanceDraft.active_theme_id) ?? themes[0];

  function changeAppearance(next: AppearancePreferences) {
    setAppearanceDraft(next);
    setAppearanceDirty(true);
    setMessage(null);
    setMessageIsError(false);
  }

  function chooseTheme(themeId: string) {
    changeAppearance(selectTheme(appearanceDraft, themeId));
  }

  function changeSelectedTheme(nextTheme: SavedTheme) {
    const next = cloneAppearance(appearanceDraft);
    next.themes = next.themes.map((theme) => theme.id === nextTheme.id ? { ...nextTheme, name: nextTheme.name.slice(0, 40) } : theme);
    changeAppearance(next);
  }

  function openCreate(base: ThemeMode = selectedTheme.mode) {
    setNewThemeBase(base);
    setNewThemeName(`My ${base === "light" ? "Light" : "Dark"} Theme`);
    setCreateOpen(true);
  }

  function createTheme() {
    if (!newThemeName.trim() || appearanceDraft.themes.length >= MAX_SAVED_THEMES) return;
    const base = BUILT_IN_THEMES.find((theme) => theme.mode === newThemeBase) ?? BUILT_IN_THEMES[0];
    const created: SavedTheme = {
      id: createThemeId(),
      name: newThemeName.trim().slice(0, 40),
      mode: base.mode,
      colors: { ...base.colors },
    };
    const next = cloneAppearance(appearanceDraft);
    next.themes.push(created);
    changeAppearance(selectTheme(next, created.id));
    setCreateOpen(false);
    setMessage("New theme added to your draft. Customize it, then save.");
  }

  function deleteSelectedTheme() {
    if (isBuiltInTheme(selectedTheme.id)) return;
    const next = cloneAppearance(appearanceDraft);
    next.themes = next.themes.filter((theme) => theme.id !== selectedTheme.id);
    changeAppearance(selectTheme(next, selectedTheme.mode === "dark" ? "builtin-dark" : "builtin-light"));
    setMessage("Theme removed from your draft. Save to confirm the change.");
  }

  function discardAppearance() {
    setAppearanceDraft(cloneAppearance(normalizeAppearance(preferences?.appearance)));
    setAppearanceDirty(false);
    setMessageIsError(false);
    setMessage("Theme changes discarded.");
  }

  async function saveAppearance() {
    setSaving(true);
    setMessage(null);
    setMessageIsError(false);
    try {
      const next = await update({ appearance: appearanceDraft });
      setAppearanceDraft(cloneAppearance(normalizeAppearance(next.appearance)));
      setAppearanceDirty(false);
      setMessage("Theme collection saved and applied to your workspace.");
    } catch (error) {
      setMessageIsError(true);
      setMessage(error instanceof Error ? error.message : "Theme could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  function exportTheme() {
    const contents = JSON.stringify(createSharedThemeFile(appearanceDraft), null, 2);
    const url = URL.createObjectURL(new Blob([contents], { type: "application/json" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${selectedTheme.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "theme"}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setMessageIsError(false);
    setMessage(`“${selectedTheme.name}” downloaded and ready to share.`);
  }

  async function importTheme(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setMessage(null);
    setMessageIsError(false);
    try {
      if (file.size > MAX_THEME_FILE_SIZE) throw new Error("Theme files must be smaller than 64 KB.");
      if (appearanceDraft.themes.length >= MAX_SAVED_THEMES) throw new Error(`You can save up to ${MAX_SAVED_THEMES} personal themes.`);
      const imported = parseSharedThemeFile(await file.text());
      const created: SavedTheme = { id: createThemeId(), ...imported };
      const next = cloneAppearance(appearanceDraft);
      next.themes.push(created);
      changeAppearance(selectTheme(next, created.id));
      setMessage(`“${created.name}” imported into your draft. Review it, then save.`);
    } catch (error) {
      setMessageIsError(true);
      setMessage(error instanceof Error ? error.message : "Theme could not be imported.");
    }
  }

  return (
    <div className="space-y-5">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-studio-accent">Visual system</p>
          <h2 className="mt-2 text-xl font-semibold tracking-tight text-studio-ink">Theme and colors</h2>
          <p className="mt-1.5 max-w-2xl text-sm leading-6 text-studio-muted">
            Create named themes, keep your favorites close, and share any palette as a JSON file.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={themeFileInputRef} type="file" accept=".json,application/json" className="sr-only" aria-label="Choose a theme file" onChange={(event) => void importTheme(event)} />
          <Button type="button" variant="outline" size="sm" disabled={isLoading || saving} onClick={() => themeFileInputRef.current?.click()}>
            <Upload className="h-3.5 w-3.5" /> Import theme
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={isLoading} onClick={exportTheme}>
            <Download className="h-3.5 w-3.5" /> Export selected
          </Button>
        </div>
      </div>

      <section aria-labelledby="theme-library-title">
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <h3 id="theme-library-title" className="text-sm font-semibold text-studio-ink">Your themes</h3>
            <p className="mt-1 text-xs text-studio-muted">Select a theme to preview or edit it.</p>
          </div>
          <span className="font-mono text-[10px] text-studio-muted">{appearanceDraft.themes.length}/{MAX_SAVED_THEMES} PERSONAL</span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
          <button
            type="button"
            disabled={appearanceDraft.themes.length >= MAX_SAVED_THEMES}
            onClick={() => openCreate("light")}
            className="flex min-h-24 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-studio-border bg-studio-card/40 text-studio-muted transition-colors hover:border-studio-accent hover:bg-studio-accent-soft hover:text-studio-accent focus-visible:outline-none focus-visible:shadow-glow-sm disabled:opacity-50"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full border border-current"><Plus className="h-4 w-4" /></span>
            <span className="text-xs font-semibold">New theme</span>
          </button>
          {themes.map((theme) => <ThemeCard key={theme.id} theme={theme} selected={theme.id === appearanceDraft.active_theme_id} onClick={() => chooseTheme(theme.id)} />)}
        </div>
      </section>

      <ThemeEditor
        key={selectedTheme.id}
        theme={selectedTheme}
        builtIn={isBuiltInTheme(selectedTheme.id)}
        onChange={changeSelectedTheme}
        onSave={() => void saveAppearance()}
        onDiscard={discardAppearance}
        onCreateCopy={() => openCreate(selectedTheme.mode)}
        onDelete={deleteSelectedTheme}
        dirty={appearanceDirty}
        saving={saving}
        disabled={isLoading}
      />

      {message && <p role={messageIsError ? "alert" : "status"} className={messageIsError ? "text-xs font-medium text-studio-danger" : "text-xs font-medium text-studio-success"}>{message}</p>}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} title="Create a new theme">
        <div className="space-y-5 p-4 sm:p-5">
          <label className="block text-xs font-semibold text-studio-muted">
            Theme name
            <Input className="mt-2" autoFocus maxLength={40} value={newThemeName} onChange={(event) => setNewThemeName(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") createTheme(); }} />
          </label>
          <fieldset>
            <legend className="text-xs font-semibold text-studio-muted">Start from</legend>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {BUILT_IN_THEMES.map((theme) => {
                const Icon = theme.mode === "light" ? Sun : Moon;
                const active = newThemeBase === theme.mode;
                return (
                  <button key={theme.id} type="button" onClick={() => setNewThemeBase(theme.mode)} className={cn("rounded-xl border p-3 text-left transition-colors", active ? "border-studio-accent bg-studio-accent-soft" : "border-studio-border bg-studio-card hover:border-studio-border-light")}>
                    <div className="flex items-center justify-between"><ThemeSwatches theme={theme} /><Icon className="h-4 w-4 text-studio-muted" /></div>
                    <p className="mt-3 text-xs font-semibold text-studio-ink">{theme.name}</p>
                  </button>
                );
              })}
            </div>
          </fieldset>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button disabled={!newThemeName.trim()} onClick={createTheme}><Plus className="h-4 w-4" /> Create theme</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
